/**
 * `/recruiting/jobs` — the Requisitions screen, WP-W1. `web/shapes/recruiting-jobs.md`
 * is the agreed shape, written before this file.
 *
 * The first recruiting surface in the SPA, and the pattern the later recruiting
 * screens copy: a company-scoped **cursor** list with the server's `status` and
 * `owner=me` filters, a create form that opens a requisition in `draft`, and a detail
 * that edits fields, moves the status through the state machine, and manages the
 * requisition's postings.
 *
 * ── Not a sensitive-access surface ──────────────────────────────────────────
 *
 * Unlike `/incidents` and `/alerts`, the recruiting requisition endpoints carry no
 * `x-wp-sensitive-access`, so there is **no `X-Access-Reason`** gate: nothing here
 * sends one, because collecting a reason the server neither requires nor stores is
 * collecting it under false pretences. The screen reads on `recruiting.view_jobs` and
 * writes on `recruiting.manage_jobs`; the server runs that chain on every call.
 *
 * ── What each refusal does to the screen ────────────────────────────────────
 *
 * Nothing is applied optimistically, so there is no rollback to get wrong. A read
 * refused renders `ApiErrorNote` in that region and leaves the others standing; a
 * write refused renders the server's own sentence and leaves the form's unsaved input
 * in place. A status move refused `409 WORKFORCE_INVALID_STATE_TRANSITION` also
 * re-reads, because in that one case the state the controls were drawn from is wrong.
 */

import { useMemo, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { useSession } from '../../auth/SessionProvider';
import type {
  RecruitingPosting,
  RecruitingPostingCreateRequest,
  RecruitingPostingListResponse,
  RecruitingRequisition,
  RecruitingRequisitionCreateRequest,
  RecruitingRequisitionListResponse,
  RecruitingRequisitionUpdateRequest,
  RequisitionStatus,
} from '../../api/recruiting';
import {
  RECRUITING_REQUISITIONS_PATH,
  recruitingPostingsPath,
  recruitingRequisitionPath,
  recruitingRequisitionsPath,
} from '../../api/recruiting';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import {
  REQUISITION_STATUSES,
  STATUS_BADGE,
  STATUS_LABEL,
  isRequisitionStatus,
} from '../../recruiting/requisition';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { PartialData } from '../../states/PartialData';
import { Skeleton } from '../../states/Skeleton';
import { SEVERITY_ICON } from '../../states/icons';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { CreateRequisitionForm } from './CreateRequisitionForm';
import { PostingsPanel } from './PostingsPanel';
import { RequisitionEditForm } from './RequisitionEditForm';
import { RequisitionStatusControls } from './RequisitionStatusControls';

const PAGE_LIMIT = 50;
const DEFAULT_TIMEZONE = 'Asia/Ho_Chi_Minh';

export function JobsScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? DEFAULT_TIMEZONE;

  const [statusFilter, setStatusFilter] = useState<'' | RequisitionStatus>('');
  const [ownerMe, setOwnerMe] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [listToken, setListToken] = useState(0);
  const [detailToken, setDetailToken] = useState(0);
  const [postingsToken, setPostingsToken] = useState(0);

  const listPath = recruitingRequisitionsPath({
    limit: PAGE_LIMIT,
    status: statusFilter === '' ? undefined : statusFilter,
    owner: ownerMe ? 'me' : undefined,
  });
  const list = useApiQuery<RecruitingRequisitionListResponse>(listPath, listToken);

  const detailPath = selectedId === null ? null : recruitingRequisitionPath(selectedId);
  const detail = useApiQuery<RecruitingRequisition>(detailPath, detailToken);

  const postingsPath = selectedId === null ? null : recruitingPostingsPath(selectedId);
  const postings = useApiQuery<RecruitingPostingListResponse>(postingsPath, postingsToken);

  const createReq = useApiMutation<RecruitingRequisitionCreateRequest, RecruitingRequisition>(
    RECRUITING_REQUISITIONS_PATH,
    'POST',
  );
  // Two PATCH mutations on the same endpoint, kept apart so a field save and a status
  // move never share a pending/error state — the field form and the status controls
  // are two different questions the operator asks of one requisition.
  const fieldPatch = useApiMutation<RecruitingRequisitionUpdateRequest, RecruitingRequisition>(
    recruitingRequisitionPath('unset'),
    'PATCH',
  );
  const statusPatch = useApiMutation<RecruitingRequisitionUpdateRequest, RecruitingRequisition>(
    recruitingRequisitionPath('unset'),
    'PATCH',
  );
  const createPosting = useApiMutation<RecruitingPostingCreateRequest, RecruitingPosting>(
    recruitingPostingsPath('unset'),
    'POST',
  );

  const rows: readonly RecruitingRequisition[] = useMemo(() => list.data?.data ?? [], [list.data]);
  const hasMore = list.data?.page.has_more ?? false;
  const current = detail.data;

  const settle = (): void => {
    setDetailToken((value) => value + 1);
    setListToken((value) => value + 1);
  };

  const submitCreate = (body: RecruitingRequisitionCreateRequest): void => {
    createReq
      .submit(body)
      .then(() => {
        // A new requisition changes the list this screen is showing.
        setListToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  const moveStatus = (to: RequisitionStatus): void => {
    if (selectedId === null) return;
    statusPatch
      .submit({ status: to }, recruitingRequisitionPath(selectedId))
      .then(settle)
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const saveFields = (body: RecruitingRequisitionUpdateRequest): void => {
    if (selectedId === null) return;
    fieldPatch
      .submit(body, recruitingRequisitionPath(selectedId))
      .then(settle)
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const submitPosting = (body: RecruitingPostingCreateRequest): void => {
    if (selectedId === null) return;
    createPosting
      .submit(body, recruitingPostingsPath(selectedId))
      .then(() => {
        setPostingsToken((value) => value + 1);
      })
      .catch(() => {
        /* resolves rather than rejects */
      });
  };

  const openRequisition = (id: string): void => {
    fieldPatch.reset();
    statusPatch.reset();
    createPosting.reset();
    setSelectedId(id);
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.recruitingJobs')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('recruiting.jobs.intro')}</p>
        <p className="wp-small wp-muted" data-testid="w1-scope-note">
          {t('recruiting.jobs.scopeNote')}
        </p>
      </div>

      <div className="wp-filters">
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w1-status-filter">
            {t('recruiting.jobs.filter.status')}
          </label>
          <select
            className="wp-field__input"
            id="w1-status-filter"
            data-testid="w1-status-filter"
            value={statusFilter}
            onChange={(event) => {
              const next = event.target.value;
              setStatusFilter(next === '' ? '' : (next as RequisitionStatus));
              setSelectedId(null);
            }}
          >
            <option value="">{t('filters.all')}</option>
            {REQUISITION_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(STATUS_LABEL[status])}
              </option>
            ))}
          </select>
        </div>
        <div className="wp-filters__group">
          <label className="wp-toggle" htmlFor="w1-owner-me">
            <input
              id="w1-owner-me"
              type="checkbox"
              checked={ownerMe}
              aria-describedby="w1-owner-me-hint"
              data-testid="w1-owner-me"
              onChange={(event) => {
                setOwnerMe(event.target.checked);
                setSelectedId(null);
              }}
            />
            <span>{t('recruiting.jobs.filter.ownerMe')}</span>
          </label>
          <p className="wp-small wp-muted" id="w1-owner-me-hint">
            {t('recruiting.jobs.filter.ownerMeHint')}
          </p>
        </div>
      </div>

      {list.status === 'loading' ? (
        <Skeleton
          rows={4}
          variant="block"
          label={t('recruiting.jobs.subjectWord')}
          testId="w1-list-skeleton"
        />
      ) : null}
      {list.status === 'error' && list.error !== null ? (
        <ApiErrorNote
          error={list.error}
          onRetry={list.reload}
          subject={t('recruiting.jobs.subjectWord')}
          testId="w1-list-error"
        />
      ) : null}

      {list.data === null ? null : rows.length === 0 ? (
        <EmptyState
          title={t('recruiting.jobs.empty.title')}
          description={t('recruiting.jobs.empty.body', {
            status: statusFilter === '' ? t('filters.all') : t(STATUS_LABEL[statusFilter]),
          })}
          testId="w1-list-empty"
        />
      ) : (
        <>
          {hasMore ? (
            <PartialData
              excludes={t('recruiting.jobs.partial.excludes', { shown: rows.length })}
              testId="w1-list-partial"
            />
          ) : null}
          <ul className="wp-rows" data-testid="w1-list">
            {rows.map((requisition) => {
              const badge = isRequisitionStatus(requisition.status)
                ? STATUS_BADGE[requisition.status]
                : 'info';
              return (
                <li
                  className="wp-row"
                  key={requisition.id}
                  data-testid={`w1-req-${requisition.id}`}
                >
                  <div className="wp-row__main">
                    <p>
                      <Badge
                        severity={badge}
                        icon={SEVERITY_ICON[badge]}
                        label={
                          isRequisitionStatus(requisition.status)
                            ? t(STATUS_LABEL[requisition.status])
                            : requisition.status
                        }
                        testId={`w1-req-status-${requisition.id}`}
                      />{' '}
                      <span>{requisition.title}</span>
                    </p>
                    <p className="wp-small wp-muted">
                      {t('recruiting.jobs.row.meta', {
                        headcount: requisition.headcount,
                        type:
                          requisition.employment_type === null
                            ? t('recruiting.jobs.employmentType.none')
                            : t(`recruiting.jobs.employmentType.${requisition.employment_type}`),
                        location: requisition.location ?? t('recruiting.jobs.detail.noLocation'),
                      })}
                    </p>
                  </div>
                  <div className="wp-row__figure">
                    <button
                      className="wp-button"
                      type="button"
                      data-testid={`w1-open-${requisition.id}`}
                      onClick={() => {
                        openRequisition(requisition.id);
                      }}
                    >
                      {t('recruiting.jobs.open')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {selectedId === null ? null : (
        <>
          {detail.status === 'loading' ? (
            <Skeleton
              rows={5}
              variant="block"
              label={t('recruiting.jobs.detail.subject')}
              testId="w1-detail-skeleton"
            />
          ) : null}
          {detail.status === 'error' && detail.error !== null ? (
            <ApiErrorNote
              error={detail.error}
              onRetry={detail.reload}
              subject={t('recruiting.jobs.detail.subject')}
              testId="w1-detail-error"
            />
          ) : null}

          {current === null ? null : (
            <section
              className="wp-section"
              aria-labelledby="w1-detail-heading"
              data-testid="w1-detail"
            >
              <h2 className="wp-section__title" id="w1-detail-heading" tabIndex={-1}>
                {current.title}
              </h2>
              <p>
                <Badge
                  severity={
                    isRequisitionStatus(current.status) ? STATUS_BADGE[current.status] : 'info'
                  }
                  icon={
                    SEVERITY_ICON[
                      isRequisitionStatus(current.status) ? STATUS_BADGE[current.status] : 'info'
                    ]
                  }
                  label={
                    isRequisitionStatus(current.status)
                      ? t(STATUS_LABEL[current.status])
                      : current.status
                  }
                  testId="w1-detail-status"
                />
              </p>
              <p className="wp-small wp-muted">
                {t('recruiting.jobs.detail.meta', {
                  headcount: current.headcount,
                  recruiter:
                    current.recruiter_employee_id ?? t('recruiting.jobs.detail.unassigned'),
                })}
              </p>
              <p className="wp-small wp-muted">
                {current.opened_at === null
                  ? t('recruiting.jobs.detail.notOpened')
                  : t('recruiting.jobs.detail.opened', {
                      when: formatInstant(current.opened_at, locale, timezone),
                    })}
                {current.closed_at === null
                  ? ''
                  : ` · ${t('recruiting.jobs.detail.closed', {
                      when: formatInstant(current.closed_at, locale, timezone),
                    })}`}
              </p>
              {/* The persistent last-updated line (DESIGN.md §8), the read's own instant. */}
              <p className="wp-small wp-muted" data-testid="w1-last-updated">
                {t('recruiting.jobs.lastUpdated', {
                  when: formatInstant(current.updated_at, locale, timezone),
                })}
              </p>

              <RequisitionStatusControls
                status={isRequisitionStatus(current.status) ? current.status : 'draft'}
                pending={statusPatch.status === 'pending'}
                error={statusPatch.error}
                onMove={moveStatus}
              />

              <RequisitionEditForm
                key={`${current.id}:${current.updated_at}`}
                requisition={current}
                pending={fieldPatch.status === 'pending'}
                error={fieldPatch.error}
                saved={fieldPatch.status === 'ok'}
                onSubmit={saveFields}
              />

              <PostingsPanel
                status={postings.status}
                postings={postings.data?.data ?? null}
                readError={postings.error}
                onReload={postings.reload}
                timezone={timezone}
                createPending={createPosting.status === 'pending'}
                createError={createPosting.error}
                created={createPosting.status === 'ok' ? createPosting.data : null}
                onCreate={submitPosting}
              />
            </section>
          )}
        </>
      )}

      {/* The create form is the last section, not the first: a recruiter opens this
          screen to read the list, and opening a new one is the occasional action.
          Leading with the form put the subject of the page below the fold. */}
      <CreateRequisitionForm
        pending={createReq.status === 'pending'}
        error={createReq.error}
        created={createReq.status === 'ok' ? createReq.data : null}
        onSubmit={submitCreate}
      />
    </div>
  );
}
